import { BaseControl, IControlParameters } from './BaseControl';
import { MulticastDelegateBag } from '@Common/Core/MulticastDelegate';
import { Button } from '@Common/Controls/SimpleButton';
import { enumerable } from '@Common/Core/CoreDecorator';

export interface IBrowseLabelParameters<TValue = any> extends IControlParameters {
    valuePath?: (value: TValue) => string;
    useBorder?: boolean;
    useText?: boolean;
    useHint?: boolean;
    showClearButton?: boolean;
    value?: any;
}

export class BrowseLabel<TValue = any> extends BaseControl {
    public public;
    private readonly eventBag = new MulticastDelegateBag();
    public readonly onChange = this.eventBag.create<IHandler>();
    public readonly onDetailsClick = this.eventBag.create<IHandler>();
    public readonly onValidate = this.eventBag.create<ICallback<boolean>>();
    public readonly onClear = this.eventBag.create<IHandler>();
    private readonly btn: Button;
    private readonly clearBtn: Button;
    private readonly labelWithHintControl: JQuery;
    private readonly labelControl: JQuery;
    private readonly hintControl: JQuery;
    private currentObject: TValue;

    constructor(private readonly param: IBrowseLabelParameters<TValue>) {
        super(param);
        _.defaults(param, {
            css: null,
            className: '',
            width: 'auto',
            useText: true,
            useHint: false,
            showClearButton: false,
            validator: null,
            valuePath: value => (_.isNil(value) ? '' : value.toString())
        });

        this._root = $('<div class="browseLabel" />')
            .css({ width: param.width })
            .on('click', () => this.btn.getControl().trigger('click'));

        this.btn = new Button({ width: '', height: '', text: '...' });
        this.btn.onClick(this.onDetailsClick.toFunction());
        this.btn.getControl().toggleClass('withText', param.useText).appendTo(this._root);

        if (param.showClearButton) {
            this.clearBtn = new Button({ width: '', height: '', text: strings.clear });
            this.clearBtn.getControl().prependTo(this._root);
            this.clearBtn.onClick(() => {
                this.onClear.fire();
            });
        }

        if (param.useHint && param.useText) {
            this.labelWithHintControl = $('<div class="label" />');
            this.labelControl = $('<div />').css({ float: 'left' }).appendTo(this.labelWithHintControl);
            this.hintControl = $('<div />').css({ float: 'right', color: 'gray' }).appendTo(this.labelWithHintControl);
            this.labelWithHintControl.appendTo(this._root);
        } else if (param.useText) {
            this.labelControl = $('<div class="label">').appendTo(this._root);
        }

        param.className && this._root.addClass(param.className);
        param.css && this._root.css(param.css);

        this.currentObject = param.value;

        this.refreshDisplayValue();
    }

    @enumerable
    get useHint(): boolean {
        return !!this.hintControl;
    }

    public getMinRightWidth(): number {
        return (
            (this.labelControl && this.labelControl.textWidth()) +
            (this.hintControl ? this.hintControl.textWidth() : 0) +
            (this.clearBtn ? this.clearBtn.getControl().outerWidth() + 6 : 0) +
            this.btn.getControl().outerWidth() +
            6
        );
    }

    public refreshDisplayValue(): void {
        this.labelControl && this.labelControl.text(this.param.valuePath(this.currentObject));
    }

    public setValue(value: TValue): void {
        this.currentObject = value;
        this.validate();
        this.onChange();
        this.refreshDisplayValue();
    }

    public getValue(): TValue {
        return this.currentObject;
    }

    public validate(): void {
        const value = this.currentObject;
        let state = true;
        if (_.isFunction(this.param.validator)) {
            state = this.param.validator(value);
        }
        this.onValidate(state);
    }

    public setHint(value: string): void {
        this.hintControl && this.hintControl.text(value);
    }

    public setError(msg: string): void {
        this._root.addClass('invalid');
        this._root.attr('title', msg);
    }

    public removeError(): void {
        this._root.removeClass('invalid');
        this._root.attr('title', '');
    }

    public spinnerStart(message: string = null): void {
        this._root.spinnerStart({ isSmall: true, message: message });
    }

    public spinnerStop(): void {
        this._root.spinnerStop();
    }

    public adjust(width?: number, height?: number): void {
        this._root.css({
            width: width ? width : null,
            height: height ? height : null
        });
    }

    public enabled(value?: boolean): boolean {
        return this.setState(value);
    }

    public setState(enabled: boolean): boolean {
        this.btn.enabled(enabled);
        this.clearBtn && this.clearBtn.enabled(enabled);
        this.labelControl && this.labelControl.toggleClass('disabled', !enabled);
        this.labelWithHintControl && this.labelWithHintControl.toggleClass('disabled', !enabled);
        return super.enabled(enabled);
    }

    readonly(data?: boolean): boolean {
        if (_.isBoolean(data)) {
            super.setState(data);
        }
        return this.enabled();
    }

    public getState(): boolean {
        return this.enabled();
    }

    public setValuePath(valuePath: any): void {
        this.param.valuePath = valuePath;
    }

    public destroy(): void {
        this.btn.destroy();
        this.clearBtn && this.clearBtn.destroy();
        this.eventBag.destroy();
        this.labelControl && this.labelControl.remove();
        this.labelWithHintControl && this.labelWithHintControl.remove();
        super.destroy();
    }
}
